'use client';

import { useMemo } from 'react';
import type { ReactNode } from 'react';
import { DateInput as MantineDateInput } from '@mantine/dates';
import type { DateInputProps as MantineDateInputProps } from '@mantine/dates';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { useTranslation } from 'next-i18next/pages';

import { useGetDateValueFormat } from '@highlight-cp/hooks';

import type { LockedProps } from '../types';

// Mantine v8 emits date strings from onChange; we keep the pre-v8 Date-based
// public API so consumers never deal with the string format.
// `valueFormat` is locked because it is force-applied from useGetDateValueFormat below: it is the
// user's display-format preference, so a call-site override would silently defeat it. It was already
// unreachable (assigned after the prop spread) — omitting it turns a no-op prop into a type error.
// `label` is required, carried over from the DatePicker wrapper this replaced: a date field with no
// visible label has nothing to associate its input with. All call sites already pass one.
type DateInputProps = Omit<MantineDateInputProps, LockedProps | 'onChange' | 'valueFormat'> & {
  label: ReactNode;
  onChange?: (value: Date | null) => void;
};

// Global side-effect: extends dayjs so dateParser can use strict format matching.
// Safe to call multiple times — subsequent calls are no-ops.
dayjs.extend(customParseFormat);

//? DatePickerInput is a button and not as friendly for testing
export const DateInput = (props: DateInputProps) => {
  const {
    locale = typeof navigator !== 'undefined' ? navigator.language : undefined,
    onChange,
    ...rest
  } = props;
  const valueFormat = useGetDateValueFormat();
  const { t } = useTranslation();

  const dateParser = useMemo(
    () => rest.dateParser ?? ((value: string) => dayjs(value, valueFormat, true).toDate()),
    [rest.dateParser, valueFormat],
  );

  return (
    <MantineDateInput
      // Defaults, so they sit before the spread and stay overridable per call site.
      highlightToday
      clearable
      // Mantine's clear button is an icon-only CloseButton with no accessible name of its own, so
      // every clearable field would otherwise ship an unlabelled button.
      clearButtonProps={{ 'aria-label': t('Clear date') }}
      {...rest}
      locale={locale}
      valueFormat={valueFormat}
      dateParser={dateParser}
      placeholder={rest.placeholder ?? valueFormat}
      onChange={onChange && ((value) => onChange(value ? dayjs(value).toDate() : null))}
    />
  );
};
